'use client';

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslations } from 'next-intl';
import { CustomImage } from 'components/shared/custom-image/CustomImage';
import { useWindowSize } from 'hooks/dom/useWindowSize';
import { PERFORMANCE_LAB_RECOVERY_HERO_VIDEO } from './media';
import { RECOVERY_PAGE_HERO_POSTER } from './recoveryPageAssets';
import {
  DiagnosticsSignalMarquee,
  type SignalMarqueeItem,
} from './DiagnosticsSignalMarquee';
import styles from './RecoveryHeroSection.module.css';

const DESKTOP_MIN_WIDTH = 768;

/** i18n prefix for the recovery hero marquee pills (`${prefix}.${key}.label`). */
const RECOVERY_MARQUEE_PREFIX = 'recoveryPage.heroMarquee';

/** Recovery modalities shown in the recovery hero's signal marquee. */
const RECOVERY_MARQUEE_SIGNALS: ReadonlyArray<SignalMarqueeItem> = [
  { key: 'ivTherapy', default: 'IV therapy' },
  { key: 'hbot', default: 'Hyperbaric Oxygen Therapy' },
  { key: 'cryotherapy', default: 'Cryotherapy' },
  { key: 'sportsMassage', default: 'Sports massage' },
  { key: 'infraredSauna', default: 'Infrared sauna' },
  { key: 'coldPlunge', default: 'Cold plunge' },
  { key: 'inversionTable', default: 'Inversion table' },
  { key: 'massageGuns', default: 'Massage guns' },
  { key: 'powerplate', default: 'Powerplate' },
  { key: 'compressionTherapy', default: 'Compression therapy' },
  { key: 'localizedHotCold', default: 'Localized hot–cold' },
];

export const RecoveryHeroSection: React.FC = () => {
  const t = useTranslations('performance-lab');
  const heroVideoRef = useRef<HTMLVideoElement>(null);
  const { width } = useWindowSize();
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  const isDesktop = width >= DESKTOP_MIN_WIDTH;

  const resolvedHeroVideoSrc = useMemo(
    () =>
      isDesktop
        ? PERFORMANCE_LAB_RECOVERY_HERO_VIDEO.desktop
        : PERFORMANCE_LAB_RECOVERY_HERO_VIDEO.mobile,
    [isDesktop],
  );

  const resolvedHeroVideoFallbackSrc = useMemo(
    () =>
      isDesktop ? undefined : PERFORMANCE_LAB_RECOVERY_HERO_VIDEO.mobileFallback,
    [isDesktop],
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setPrefersReducedMotion(mediaQuery.matches);
    update();
    mediaQuery.addEventListener('change', update);
    return () => mediaQuery.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    setIsVideoPlaying(false);
  }, [resolvedHeroVideoSrc, prefersReducedMotion]);

  useEffect(() => {
    if (prefersReducedMotion) return;

    const video = heroVideoRef.current;
    if (!video) return;

    const kickPlayback = () => {
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      video.volume = 0;
      void video
        .play()
        .then(() => setIsVideoPlaying(true))
        .catch(() => {
          /* autoplay blocked */
        });
    };

    video.load();
    kickPlayback();

    const mediaEvents = [
      'loadeddata',
      'canplay',
      'canplaythrough',
      'loadedmetadata',
    ] as const;
    mediaEvents.forEach((ev) => video.addEventListener(ev, kickPlayback));
    window.addEventListener('pageshow', kickPlayback);

    return () => {
      mediaEvents.forEach((ev) => video.removeEventListener(ev, kickPlayback));
      window.removeEventListener('pageshow', kickPlayback);
    };
  }, [resolvedHeroVideoSrc, prefersReducedMotion]);

  const handleHeroVideoError = useCallback(() => {
    const video = heroVideoRef.current;
    if (!video || !resolvedHeroVideoFallbackSrc) return;
    if (video.src === resolvedHeroVideoFallbackSrc) return;
    video.src = resolvedHeroVideoFallbackSrc;
    video.load();
  }, [resolvedHeroVideoFallbackSrc]);

  const showPosterOnly = prefersReducedMotion || !isVideoPlaying;

  return (
    <section className={styles.section} aria-label={t('recoveryPage.hero.aria')}>
      <div className={styles.mediaFrame}>
        <div className={styles.heroMediaArea}>
          <div className={styles.background} aria-hidden="true">
            {!prefersReducedMotion && (
              <video
                className={styles.heroVideo}
                key={resolvedHeroVideoSrc}
                ref={heroVideoRef}
                src={resolvedHeroVideoSrc}
                autoPlay
                loop
                muted
                playsInline
                controls={false}
                controlsList="nodownload noplaybackrate nofullscreen"
                disablePictureInPicture
                disableRemotePlayback
                aria-hidden="true"
                preload="auto"
                onPlaying={() => setIsVideoPlaying(true)}
                onError={handleHeroVideoError}
                {...{ 'webkit-playsinline': 'true' }}
              />
            )}
            <CustomImage
              className={styles.heroPoster}
              data-hidden={
                !showPosterOnly && isVideoPlaying ? 'true' : undefined
              }
              src={RECOVERY_PAGE_HERO_POSTER}
              alt=""
              fill
              sizes="100vw"
              aria-hidden="true"
              priority
            />
            <div className={styles.overlay} />
          </div>

          <div className={styles.copyBlock}>
            <p className={styles.location}>
              {t('recoveryPage.hero.eyebrow')}
            </p>
            <div className={styles.titleWrap}>
              <h1 className={`${styles.title} pl-dharma-title`}>
                {t('recoveryPage.hero.title')}
              </h1>
            </div>
            <p className={styles.subtitle}>
              {t('recoveryPage.hero.subtitle')}
            </p>
          </div>
        </div>
      </div>

      <div className={styles.heroSignalBand}>
        <DiagnosticsSignalMarquee
          signals={RECOVERY_MARQUEE_SIGNALS}
          labelPrefix={RECOVERY_MARQUEE_PREFIX}
          ariaLabel={t('recoveryPage.heroMarquee.aria')}
        />
      </div>
    </section>
  );
};
